import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABanner } from "@/components/site/CTABanner";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { BUSINESS, IMAGES } from "@/lib/site";

const VALUES = [
  "Reliability — we show up when we say we will",
  "Attention to detail in every room",
  "Professional, friendly communication",
  "Respect for your home and workplace",
  "Flexible services that fit your schedule",
  "Consistent cleaning standards",
  "We listen to what your property actually needs",
];

const About = () => (
  <>
    <Seo
      title="About Us | Oz SpotNest Cleaning Service — Sydney NSW"
      description="Oz SpotNest Cleaning Service is a Belmore-based cleaning business founded by Vinod & Harry, providing dependable, detailed and customer-focused cleaning across Sydney, NSW."
    />
    <PageHero
      eyebrow="About us"
      title="A Fresh Approach to"
      highlight="Professional Cleaning"
      description={`${BUSINESS.fullName} was established to provide dependable, detailed and customer-focused cleaning services to homes and businesses across ${BUSINESS.area}.`}
    />

    <section className="section-pad" data-testid="about-story-section">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <span className="eyebrow">Who we are</span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
              A local team that cares about the details
            </h2>
            <p className="mt-6 text-base leading-relaxed sm:text-lg">
              Founded by {BUSINESS.founders}, {BUSINESS.name} is a proudly local cleaning business
              based in Belmore NSW. We started with a simple idea: cleaning should be
              straightforward, reliable and done properly — every visit.
            </p>
            <p className="mt-4 text-base leading-relaxed">
              As a new business, we're not going to quote you invented statistics or decades of
              history. What we can offer is genuine care for your property, clear communication from
              first enquiry to finished clean, and a service built around what your home, building
              or workplace actually needs.
            </p>
            <ul className="mt-8 space-y-3">
              {VALUES.map((v) => (
                <li key={v} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" strokeWidth={3} />
                  {v}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-sky-900/10 ring-1 ring-slate-100">
              <img
                src={IMAGES.businessCard}
                alt="Oz SpotNest Cleaning Service business card — Vinod & Harry, Founder & Director, Belmore NSW"
                loading="lazy"
                className="w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 left-6 rounded-2xl bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl ring-1 ring-slate-100">
              <p className="font-heading text-sm font-bold text-navy-800">
                {BUSINESS.founders} <span className="font-medium text-slate-500">· Founder &amp; Director</span>
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section-pad bg-slate-50" data-testid="about-approach-section">
      <div className="container-site max-w-4xl">
        <Reveal>
          <span className="eyebrow">Our approach</span>
          <blockquote className="mt-6 border-l-4 border-emerald-500 pl-6 sm:pl-8">
            <p className="font-heading text-2xl font-semibold leading-snug tracking-tight text-navy-800 sm:text-3xl">
              "We believe good cleaning is about more than making a space look clean. It's about
              paying attention to the details, respecting the property and making the experience
              straightforward for the customer."
            </p>
          </blockquote>
          <div className="mt-10">
            <Link to="/contact" data-testid="about-quote-button" className="btn-primary group">
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABanner />
  </>
);

export default About;
